import { useState, useMemo } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { APP_CONFIG } from '@/constants/constants';
import { TransactionRead, SortDirection, FormStatus } from '@/types';
import { getTransactionDoc, getTransactionDocs, addTransactionDoc, updateTransactionDoc, finalizeTransactionDoc, deleteTransactionDoc } from '@/services/firebaseApi';
import { logger } from '@/utils/secureLogger';
import { trackCreate, trackDelete, trackEdit } from '@/services/analytics';

interface UseTransactionsProps {
    storageId?: string,
    transactionId?: string,
    defaultDocLimit?: number
}

export enum TransactionsSortBy {
    ID = 'transactionId',
    CREATED_AT = 'createdAt',
    CUSTOMER = 'customerName',
}

const useTransactions = ({
  storageId,
  transactionId
}: UseTransactionsProps) => {
    const queryClient = useQueryClient();

    // Filter states
    const [sortBy, setSortBy] = useState(TransactionsSortBy.ID);
    const [sortDirection, setSortDirection] = useState(SortDirection.DESCENDING);
    const [filterQuery, setFilterQuery] = useState<string>('');
    const [typeFilter, setTypeFilter] = useState<string>('all'); // 'all', 'incoming', 'outgoing'

    // Infinite query for paginated transactions
    const {
        data: transactionsData,
        isLoading: transactionsLoading,
        error: transactionsError,
        fetchNextPage,
        hasNextPage,
        refetch: refetchTransactions
    } = useInfiniteQuery({
        queryKey: ['transactions', storageId],
        queryFn: ({ pageParam }: { pageParam: string | number | undefined }) => getTransactionDocs({
            storageId: storageId!,
            startAfterData: pageParam,
            docLimit: APP_CONFIG.DEFAULT_DOC_LIMIT,
            sortBy,
            sortDirection
        }),
        initialPageParam: undefined as string | number | undefined,
        getNextPageParam: (lastPage: TransactionRead[]) => {
            if (lastPage.length < APP_CONFIG.DEFAULT_DOC_LIMIT) {
                return undefined; // No more pages
            }
            const lastTransaction = lastPage[lastPage.length - 1];
            // Access properties that exist on Transaction type
            return sortBy === 'createdAt' ? (lastTransaction as any).createdAt : lastTransaction.transactionId;
        },
        enabled: !!(storageId && !transactionId),
        staleTime: 2 * 60 * 1000, // Transactions change frequently
        gcTime: 5 * 60 * 1000,
    });

    // Single transaction query
    const {
        data: transaction,
        isLoading: transactionLoading,
        error: transactionError,
        refetch: refetchTransaction
    } = useQuery({
        queryKey: ['transaction', storageId, transactionId],
        queryFn: () => getTransactionDoc({
            storageId: storageId!,
            transactionId: transactionId!
        }),
        enabled: !!(storageId && transactionId),
        staleTime: 2 * 60 * 1000
    });

    // Flatten the infinite query data
    const allTransactions = useMemo(() => {
        return (transactionsData?.pages.flat() as TransactionRead[]) || [];
    }, [transactionsData]);

    // Apply client-side filtering
    const filteredTransactions = useMemo(() => {
        if (!allTransactions) return [];
        
        let filtered = [...allTransactions];

        // Filter by type
        if (typeFilter && typeFilter !== 'all') {
            filtered = filtered.filter((transaction: TransactionRead) => 
                transaction.type === typeFilter
            );
        }

        // Filter by search query
        if (filterQuery) {
            const lower = filterQuery.toLowerCase();
            filtered = filtered.filter((transaction: TransactionRead) =>
                transaction.transactionId.toString().includes(lower) ||
                transaction.type.toLowerCase().includes(lower) ||
                (transaction.description && transaction.description.toLowerCase().includes(lower)) ||
                (transaction.customer?.customerName && transaction.customer.customerName.toLowerCase().includes(lower))
            );
        }

        return filtered;
    }, [allTransactions, filterQuery, typeFilter]);

    // Mutations
    const createTransactionMutation = useMutation({
        mutationFn: addTransactionDoc,
        onSuccess: () => {
            trackCreate('transaction');
            logger.info('Transaction created successfully');
            queryClient.invalidateQueries({ queryKey: ['transactions', storageId] });
            queryClient.invalidateQueries({ queryKey: ['storage', storageId] });
            queryClient.invalidateQueries({ queryKey: ['storages'] });
        },
        onError: (error) => {
            logger.error('Failed to create transaction:', error);
        }
    });

    const updateTransactionMutation = useMutation({
        mutationFn: updateTransactionDoc,
        onSuccess: () => {
            trackEdit('transaction');
            logger.info('Transaction updated successfully');
            queryClient.invalidateQueries({ queryKey: ['transactions', storageId] });
            queryClient.invalidateQueries({ queryKey: ['transaction', storageId, transactionId] });
        },
        onError: (error) => {
            logger.error('Failed to update transaction:', error);
        }
    });

    const finalizeTransactionMutation = useMutation({
        mutationFn: finalizeTransactionDoc,
        onSuccess: async (_data, variables) => {
            logger.info('Transaction finalized successfully');
            const sid = (variables as any).storageId;
            const tid = (variables as any).transactionId;
            queryClient.invalidateQueries({ queryKey: ['transactions', sid] });
            queryClient.invalidateQueries({ queryKey: ['transaction', sid, tid] });
            // Drop stale draft action docs so the list reloads published state.
            await queryClient.invalidateQueries({
                queryKey: ['actionsByPaths', sid],
                exact: false,
                refetchType: 'all',
            });
            queryClient.invalidateQueries({ queryKey: ['actions', sid], exact: false });
            queryClient.invalidateQueries({ queryKey: ['products', sid] });
            queryClient.invalidateQueries({ queryKey: ['productHistory', sid], exact: false });
            queryClient.invalidateQueries({ queryKey: ['storage', sid] });
            queryClient.invalidateQueries({ queryKey: ['storages'] });
        },
        onError: (error) => {
            logger.error('Failed to finalize transaction:', error);
        }
    });

    const deleteTransactionMutation = useMutation({
        mutationFn: deleteTransactionDoc,
        onSuccess: (_data, variables) => {
            trackDelete('transaction');
            logger.info('Transaction deleted successfully');
            const sid = (variables as any).storageId;
            const tid = (variables as any).transactionId;
            // Clear and refresh all relevant caches
            queryClient.invalidateQueries({ queryKey: ['transactions', sid] });
            queryClient.invalidateQueries({ queryKey: ['transaction', sid, tid] });
            queryClient.invalidateQueries({ queryKey: ['actionsByPaths', sid], exact: false });
            queryClient.invalidateQueries({ queryKey: ['products', sid] });
            queryClient.invalidateQueries({ queryKey: ['productHistory', sid], exact: false });
            queryClient.invalidateQueries({ queryKey: ['storage', sid] });
            queryClient.invalidateQueries({ queryKey: ['storages'] });
        },
        onError: (error) => {
            logger.error('Failed to delete transaction:', error);
        }
    });

    // Helper function to determine status
    const getStatus = (loading: boolean, error: any): FormStatus => {
        if (loading) return FormStatus.LOADING;
        if (error) {
            logger.error('Error in useTransactions:', error);
            return FormStatus.ERROR;
        }
        return FormStatus.SUCCESS;
    };

    return {
        // Single transaction
        transaction: transaction || null,
        transactionStatus: getStatus(transactionLoading, transactionError),
        
        // Multiple transactions
        transactions: allTransactions,
        filteredTransactions,
        transactionsStatus: getStatus(transactionsLoading, transactionsError),
        
        // Filtering and sorting
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        filterQuery,
        setFilterQuery,
        typeFilter,
        setTypeFilter,
        
        // Pagination
        hasNextPage: hasNextPage,
        getMore: fetchNextPage,
        
        // Mutations (async only)
        createTransactionAsync: createTransactionMutation.mutateAsync,
        isCreating: createTransactionMutation.isPending,
        updateTransactionAsync: updateTransactionMutation.mutateAsync,
        isUpdating: updateTransactionMutation.isPending,
        finalizeTransactionAsync: finalizeTransactionMutation.mutateAsync,
        isFinalizing: finalizeTransactionMutation.isPending,
        deleteTransactionAsync: deleteTransactionMutation.mutateAsync,
        isDeleting: deleteTransactionMutation.isPending,
        
        // Refresh functions
        refreshTransactions: refetchTransactions,
        refreshTransaction: refetchTransaction,
    };
};

export default useTransactions;
